import { useMemo, useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton } from '../components/ui.tsx';
import { t, tn } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { day as dayText } from '../lib/format.ts';
import { useAction, useSave } from './manage/common.tsx';

/**
 * A project on a timeline (modules/projects: project.timeline): each task as a bar from its start
 * to its deadline, arrows from a task to those that wait for it, milestones and today. A bar that
 * starts before what it waits for ends is marked; planning a task can move those waiting later.
 */

interface TlTask {
  id: string;
  name: string;
  title: string;
  done: boolean;
  start: string | null;
  deadline: string | null;
  assignee_name: string | null;
  stage_name: string;
  waiting_for: number;
  plan: { from: string; to: string } | null;
  clash: boolean;
}
interface Timeline {
  today: string;
  project: { id: string; name: string; code: string; starts: string | null; deadline: string | null; can_edit: boolean };
  tasks: TlTask[];
  links: { task: string; blocked_by: string }[];
  milestones: { id: string; name: string; due: string | null; reached_at: string | null }[];
}

const ROW = 44;
const HEAD = 52;
const LABEL = 280;
const ms = (d: string) => Date.parse(`${d}T12:00:00Z`);
const between = (a: string, b: string) => Math.round((ms(b) - ms(a)) / 86_400_000);
const plus = (d: string, n: number) => new Date(ms(d) + n * 86_400_000).toISOString().slice(0, 10);
const month = (d: string) => new Intl.DateTimeFormat(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(ms(d)));
const dayNo = (d: string) => new Date(ms(d)).getUTCDate();
const weekday = (d: string) => new Date(ms(d)).getUTCDay();

export function TimelineTab({ project, onOpen, onChange }: { project: string; onOpen: (id: string) => void; onChange: () => Promise<void> }) {
  const { data, reload } = useAction<Timeline>('project.timeline', { project });
  const [planning, setPlanning] = useState<TlTask | null>(null);
  const placed = useMemo(() => (data?.tasks ?? []).filter((x) => x.plan), [data]);
  const unplanned = (data?.tasks ?? []).filter((x) => !x.plan);
  if (!data) return <Skeleton h={360} />;
  if (!data.tasks.length) return <EmptyState icon="calendar" title={t('tl.no_tasks')} body={t('tl.no_tasks_body')} />;

  const dates = [data.today, ...placed.flatMap((x) => [x.plan!.from, x.plan!.to]), ...data.milestones.map((m) => m.due).filter((d): d is string => !!d)];
  if (data.project.starts) dates.push(data.project.starts);
  if (data.project.deadline) dates.push(data.project.deadline);
  dates.sort();
  // A few days either side, starting on a Monday.
  let from = plus(dates[0]!, -3);
  from = plus(from, -((weekday(from) + 6) % 7));
  const to = plus(dates[dates.length - 1]!, 6);
  const days = between(from, to) + 1;
  // Days wide enough to read when the plan is short; narrower for long projects.
  const w = days <= 45 ? 30 : days <= 120 ? 14 : 7;
  const width = days * w;
  const x = (d: string) => between(from, d) * w;
  const row = new Map(placed.map((p, i) => [p.id, i]));
  const height = Math.max(1, placed.length) * ROW;
  const months: { at: number; label: string }[] = [];
  for (let i = 0; i < days; i++) {
    const d = plus(from, i);
    if (i === 0 || dayNo(d) === 1) months.push({ at: i * w, label: month(d) });
  }
  const after = async () => {
    await Promise.all([reload(), onChange()]);
  };

  return (
    <section className="list-card tl" aria-labelledby="tl-title">
      <div className="list-card-head">
        <div>
          <h2 id="tl-title">{t('tl.title')}</h2>
          <p className="muted">{data.project.can_edit ? t('tl.hint_edit') : t('tl.hint')}</p>
        </div>
        <ul className="tl-legend" aria-label={t('tl.legend')}>
          <li>
            <span className="tl-key" /> {t('tl.k_planned')}
          </li>
          <li>
            <span className="tl-key done" /> {t('tl.k_done')}
          </li>
          <li>
            <span className="tl-key clash" /> {t('tl.k_clash')}
          </li>
          <li>
            <span className="tl-key-ms" aria-hidden="true" /> {t('tl.k_milestone')}
          </li>
        </ul>
      </div>
      {placed.some((p) => p.clash) ? (
        <p className="callout callout-warn tl-callout" role="status">
          <Icon name="alert" size={16} /> {tn('tl.clashes', placed.filter((p) => p.clash).length)}
        </p>
      ) : null}
      <div className="tl-frame">
        <div className="tl-labels" style={{ width: LABEL }}>
          <div className="tl-corner" style={{ height: HEAD }}>
            {t('tl.task')}
          </div>
          {placed.map((p) => (
            <button key={p.id} className="tl-label" style={{ height: ROW }} onClick={() => onOpen(p.id)}>
              <span className="tl-no">{p.name}</span>
              <span className="tl-name">{p.title}</span>
              {p.waiting_for && !p.done ? (
                <span className="tl-lock" title={t('tl.waiting')}>
                  <Icon name="lock" size={12} />
                  <span className="sr-only">{t('tl.waiting')}</span>
                </span>
              ) : null}
            </button>
          ))}
        </div>
        <div className="tl-scroll" tabIndex={0} aria-label={t('tl.chart')}>
          <div className="tl-canvas" style={{ width, height: HEAD + height }}>
            <div className="tl-head" style={{ height: HEAD }}>
              {months.map((m) => (
                <span key={m.at} className="tl-month" style={{ left: m.at }}>
                  {m.label}
                </span>
              ))}
              {Array.from({ length: days }, (_, i) => {
                const d = plus(from, i);
                // Every day when there is room; otherwise each Monday (the today line shows today).
                const daily = w >= 24;
                const show = daily || weekday(d) === 1;
                return show ? (
                  <span key={d} className={`tl-day${daily ? '' : ' week'}${d === data.today ? ' today' : ''}`} style={{ left: i * w, width: daily ? w : undefined }}>
                    {dayNo(d)}
                  </span>
                ) : null;
              })}
            </div>
            <div className="tl-grid" style={{ top: HEAD, height }}>
              {Array.from({ length: days }, (_, i) => {
                const d = plus(from, i);
                const wk = weekday(d) === 0 || weekday(d) === 6;
                return wk && w >= 14 ? <span key={d} className="tl-weekend" style={{ left: i * w, width: w }} /> : null;
              })}
              {placed.map((_, i) => (
                <span key={i} className="tl-rowline" style={{ top: (i + 1) * ROW - 1 }} />
              ))}
            </div>
            <span className="tl-today" style={{ left: x(data.today) + w / 2, top: HEAD - 6, height: height + 6 }} aria-hidden="true" />
            {data.milestones
              .filter((m) => m.due)
              .map((m) => (
                <span key={m.id} className={`tl-ms${m.reached_at ? ' reached' : ''}`} style={{ left: x(m.due!) + w / 2, top: HEAD - 14 }} title={`${m.name} · ${dayText(m.due!)}`}>
                  <span className="tl-ms-dot" aria-hidden="true" />
                  <span className="sr-only">{`${m.name} · ${dayText(m.due!)}`}</span>
                </span>
              ))}
            <svg className="tl-arrows" width={width} height={height} style={{ top: HEAD }} aria-hidden="true">
              <defs>
                <marker id="tl-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                  <path d="M0,0 L8,4 L0,8 z" className="tl-arrowhead" />
                </marker>
              </defs>
              {data.links.map((l) => {
                const a = placed.find((p) => p.id === l.blocked_by);
                const b = placed.find((p) => p.id === l.task);
                if (!a || !b) return null;
                const x1 = x(a.plan!.to) + w - 2;
                const y1 = row.get(a.id)! * ROW + ROW / 2;
                const x2 = x(b.plan!.from) + 2;
                const y2 = row.get(b.id)! * ROW + ROW / 2;
                const mid = Math.max(x1 + 8, Math.min(x2 - 8, x1 + 12));
                const bad = b.clash;
                return <path key={`${l.blocked_by}-${l.task}`} d={`M${x1},${y1} H${mid} V${y2} H${x2}`} className={`tl-link${bad ? ' bad' : ''}`} markerEnd="url(#tl-arrow)" />;
              })}
            </svg>
            {placed.map((p, i) => {
              const left = x(p.plan!.from);
              const span = (between(p.plan!.from, p.plan!.to) + 1) * w;
              const label = t('tl.bar', { name: p.name, title: p.title, from: dayText(p.plan!.from), to: dayText(p.plan!.to) });
              return (
                <button
                  key={p.id}
                  className={`tl-bar${p.done ? ' done' : ''}${p.clash ? ' clash' : ''}${p.start ? '' : ' derived'}`}
                  style={{ left: left + 2, width: Math.max(span - 4, 8), top: HEAD + i * ROW + 8, height: ROW - 16 }}
                  onClick={() => (data.project.can_edit ? setPlanning(p) : onOpen(p.id))}
                  aria-label={label}
                  title={label}
                >
                  {span >= 70 ? <span>{p.assignee_name ?? p.title}</span> : null}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {unplanned.length ? (
        <div className="tl-unplanned">
          <h3>{t('tl.unplanned')}</h3>
          <p className="muted">{t('tl.unplanned_hint')}</p>
          <ul>
            {unplanned.map((p) => (
              <li key={p.id}>
                <button className="link-button" onClick={() => onOpen(p.id)}>
                  {p.name} · {p.title}
                </button>
                {data.project.can_edit ? (
                  <Button size="sm" icon="calendar" onClick={() => setPlanning(p)} aria-label={t('tl.plan_task', { name: p.name })}>
                    {t('tl.plan')}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {planning ? <PlanDialog task={planning} today={data.today} onClose={() => setPlanning(null)} onOpen={() => (setPlanning(null), onOpen(planning.id))} onDone={after} /> : null}
    </section>
  );
}

function PlanDialog({ task, today, onClose, onOpen, onDone }: { task: TlTask; today: string; onClose: () => void; onOpen: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [start, setStart] = useState(task.plan?.from ?? today);
  const [end, setEnd] = useState(task.plan?.to ?? today);
  const [shift, setShift] = useState(true);
  const bad = !start || !end || start > end;
  return (
    <Dialog
      title={t('tl.plan_title', { name: task.name })}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button variant="ghost" icon="external" onClick={onOpen}>
            {t('tl.open_task')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={bad}
            onClick={() =>
              void run(async () => {
                const r = await action<{ moved: { name: string }[] }>('project.schedule_task', { task: task.id, start, deadline: end, shift });
                await onDone();
                onClose();
                return r;
              }, t('tl.planned'))
            }
          >
            {t('tl.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <p className="muted">{task.title}</p>
        <div className="form-grid">
          <Field label={t('tl.start')}>
            <input className="input" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </Field>
          <Field label={t('tl.end')} error={start && end && start > end ? t('tl.e_order') : null}>
            <input className="input" type="date" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} />
          </Field>
        </div>
        <label className="check-row">
          <input type="checkbox" checked={shift} onChange={(e) => setShift(e.target.checked)} />
          <span>
            {t('tl.shift')}
            <span className="muted">{t('tl.shift_hint')}</span>
          </span>
        </label>
      </div>
    </Dialog>
  );
}
